/**
 * Booking pricing & timing constants. These are the canonical values used
 * by both web and mobile checkout flows; the api-server enforces the
 * matching rules at booking creation time.
 */

/** Free-cancellation window measured in hours before pickup. */
export const CANCELLATION_FREE_WINDOW_HOURS = 48;

/**
 * Host acceptance window for request-to-book trips, in hours. The
 * api-server's `lib/booking-windows.ts` re-exports this value (it is the
 * single source of truth) and derives `REQUEST_OFFER_EXPIRATION_MS` from
 * it; the web checkout uses it to render the "card authorised, only
 * charged if the host accepts within Xh" hold notice (task #6588), so
 * copy and enforcement can never drift.
 * History: 24h → 72h (task #550) → 48h (task #5598).
 */
export const REQUEST_OFFER_EXPIRATION_HOURS = 48;

/**
 * The deposit (security hold) is captured this many hours before pickup,
 * so we can authorize it on the renter's payment method while still
 * giving them time to fix a card issue.
 */
export const DEPOSIT_CAPTURE_LEAD_HOURS = 72;

/**
 * Renter service fee applied on top of the daily rate × chargeable days.
 * The live quote engine reads `platform_config.service_fee_percent` and falls
 * back to this default (15%) when the config row is absent.
 */
export const DEFAULT_SERVICE_FEE_PERCENT = 0.15;

/**
 * Default license-verification surcharge (USD) rolled into the Service fee
 * total for self-drive bookings that require license verification (Didit /
 * SambaSafety MVR check). Configurable per-deploy via
 * `platform_config.verification_fee_usd`. Task #1489.
 *
 * Renters never see this as a separate line — it is folded into the existing
 * "Service fee" displayed at checkout. The amount is tracked separately on
 * the booking row (`trips.verification_fee` + `verification_fee_usd`) so
 * finance reporting can attribute it.
 */
export const VERIFICATION_FEE_DEFAULT_USD = 10;

/**
 * Decide whether a booking is still inside the free-cancellation window.
 */
export function isFreeCancellationAllowed(
  pickupAt: string | Date,
  now: Date = new Date(),
): boolean {
  const pickup = pickupAt instanceof Date ? pickupAt : new Date(pickupAt);
  if (isNaN(pickup.getTime())) return false;
  const ms = pickup.getTime() - now.getTime();
  if (ms <= 0) return false;
  const hours = ms / (60 * 60 * 1000);
  return hours >= CANCELLATION_FREE_WINDOW_HOURS;
}

/**
 * Event-rental base rental fee — TRUE 24-hour billing rule (task #2751).
 *
 * Worked-examples table (£50/h + £200/day + £100 min listing):
 *
 *   Pickup → Dropoff             | Hours | Charge | Reasoning
 *   ---------------------------- | ----- | ------ | ---------------------------------
 *   10:00 → 11:00  same day      |  1h   | £100   | 1 × £50 = £50, lifted to £100 min
 *   10:00 → 14:00  same day      |  4h   | £200   | 4 × £50 = £200, hits daily cap
 *   10:00 → 18:00  same day      |  8h   | £200   | 8 × £50 = £400 → capped at £200
 *   Jun 1 10:00 → Jun 2 10:00    | 24h   | £200   | exactly 1 day
 *   Jun 1 10:00 → Jun 2 11:00    | 25h   | £400   | spills past 24h → 2 daily rates
 *   Jun 1 10:00 → Jun 3 10:00    | 48h   | £400   | exactly 2 days
 *   Jun 1 10:00 → Jun 3 11:00    | 49h   | £600   | spills past 48h → 3 daily rates
 *
 * Rule:
 *  1. Take the real elapsed duration in hours between pickup and dropoff
 *     (date + time, both ends).
 *  2. If a 24-hour daily rate is configured:
 *     - duration ≤ 24h: `max(minPrice, min(hourlyRate × hours, dailyRate))`.
 *       The hourly rate (if set) bills until it reaches the daily cap.
 *     - duration > 24h: `ceil(duration / 24) × dailyRate`. Any spillover past
 *       a 24h boundary triggers a full next daily rate.
 *  3. If no hourly rate is set, every started 24h period bills at the daily
 *     rate (`ceil(duration / 24) × dailyRate`) with `minPrice` as a floor.
 *  4. If no daily rate is set (hourly-only listing), it's
 *     `max(minPrice, hourlyRate × min(hours, 10 × periods))` — i.e. at most
 *     10 billable hours per started 24-hour period (task #6588). Without
 *     this cap an hourly-only listing billed the raw elapsed duration
 *     (e.g. a 24h event at DKK 1,050/h quoted DKK 25,200); a listing with
 *     a real daily rate was always capped by rule 2, so only the
 *     hourly-only branch could blow up. When the cap engages,
 *     `hourlyCapApplied` is true so quote surfaces can warn.
 *
 * The previous implementation (pre-#2751) used a same-day `dropoffTime −
 * pickupTime` delta and treated `dailyRate` as a ceiling rather than a
 * floor, which under-billed every multi-day event since 8 Apr 2026.
 */
export interface EventPricingInput {
  /** Elapsed wall-clock hours from pickup to dropoff (date+time, both ends). */
  elapsedHours: number;
  hourlyRate: number;
  dailyRate: number;
  /** Per-booking floor — `event_min_price_hourly_rate` or listing `minimum_price`. */
  minimumPrice: number;
}

export interface EventPricingResult {
  /** Rental fee in major units, rounded to 2dp. */
  rentalFee: number;
  /** Number of whole 24-hour periods billed (`ceil(elapsed/24)`, min 1). */
  chargeablePeriods: number;
  /**
   * Billing-mode signal mirroring the api-server quote response so the
   * rolled-up-price helper renders the correct headline label.
   *   - "hourly":       sub-day hourly mode, display as `${rate}/hour × Xh`.
   *   - "daily-capped": sub-day where hourly × hours hit the daily cap,
   *                     display as `${rate}/day × 1 day`.
   *   - "daily-flat":   multi-day or daily-only, display as `${rate}/day × N`.
   */
  billing: "hourly" | "daily-capped" | "daily-flat";
  /** Effective per-unit rate that matches `billing`. */
  perUnitRate: number;
  /** Billable hours when `billing === "hourly"`; 0 otherwise. */
  hoursBilled: number;
  /** True iff `minimumPrice` lifted the rental fee above the rule's output. */
  minimumPriceApplied: boolean;
  /**
   * Task #6588 — true iff this is an hourly-only listing (no positive
   * daily rate) whose elapsed hours exceeded the 10-hours-per-24h-period
   * ceiling and got capped. Quote surfaces should emit a warning so ops
   * can spot listings that need a daily rate configured.
   */
  hourlyCapApplied: boolean;
}

/**
 * Task #6588 — maximum billable hours per started 24-hour period for
 * hourly-only event listings (no positive daily rate configured).
 */
export const HOURLY_ONLY_MAX_BILLABLE_HOURS_PER_DAY = 10;

export function computeEventBaseRentalFee(input: EventPricingInput): EventPricingResult {
  const elapsed = Number.isFinite(input.elapsedHours) && input.elapsedHours > 0
    ? input.elapsedHours
    : 0;
  const hourlyRate = Math.max(0, Number(input.hourlyRate) || 0);
  const dailyRate = Math.max(0, Number(input.dailyRate) || 0);
  const minimum = Math.max(0, Number(input.minimumPrice) || 0);

  // Empty / zero duration: bill a single 24h period (or one hourly hour) so
  // the floor still applies. The api-server already validates dateFrom ≤
  // dateTo upstream, but a same-instant booking shouldn't crash here.
  const periods = elapsed <= 0 ? 1 : Math.max(1, Math.ceil(elapsed / 24));

  let rentalFee = 0;
  let billing: EventPricingResult["billing"] = "daily-flat";
  let perUnitRate = dailyRate;
  let hoursBilled = 0;
  let hourlyCapApplied = false;

  if (dailyRate > 0 && hourlyRate > 0) {
    if (elapsed > 0 && elapsed <= 24) {
      const hourlyTotal = hourlyRate * elapsed;
      if (hourlyTotal >= dailyRate) {
        rentalFee = dailyRate;
        billing = "daily-capped";
        perUnitRate = dailyRate;
      } else {
        rentalFee = hourlyTotal;
        billing = "hourly";
        perUnitRate = hourlyRate;
        hoursBilled = elapsed;
      }
    } else {
      rentalFee = periods * dailyRate;
      billing = "daily-flat";
      perUnitRate = dailyRate;
    }
  } else if (dailyRate > 0) {
    rentalFee = periods * dailyRate;
    billing = "daily-flat";
    perUnitRate = dailyRate;
  } else if (hourlyRate > 0) {
    // Hourly-only listing (no daily cap configured): cap billable hours at
    // 10 per started 24h period so a long elapsed window can't produce an
    // absurd quote (task #6588 — the DKK 25,200 = 24h × 1,050 case).
    const hoursCeiling = HOURLY_ONLY_MAX_BILLABLE_HOURS_PER_DAY * periods;
    const cappedHours = Math.min(elapsed, hoursCeiling);
    hourlyCapApplied = elapsed > hoursCeiling;
    rentalFee = hourlyRate * cappedHours;
    billing = "hourly";
    perUnitRate = hourlyRate;
    hoursBilled = cappedHours;
  }

  const beforeFloor = rentalFee;
  if (minimum > 0 && rentalFee < minimum) {
    rentalFee = minimum;
  }

  return {
    rentalFee: Math.round(rentalFee * 100) / 100,
    chargeablePeriods: periods,
    billing,
    perUnitRate,
    hoursBilled,
    minimumPriceApplied: rentalFee > beforeFloor,
    hourlyCapApplied,
  };
}

/**
 * Wall-clock elapsed hours between a pickup and dropoff. Mirrors how the
 * api-server combines `dateFrom + pickupTime` and `dateTo + dropoffTime`
 * (encoded as wall-clock UTC) before subtracting. Returns 0 for invalid
 * input so `computeEventBaseRentalFee` falls back to its floor.
 */
export function elapsedHoursBetween(
  dateFrom: string,
  dateTo: string,
  pickupTime?: string | null,
  dropoffTime?: string | null,
): number {
  if (!dateFrom || !dateTo) return 0;
  const pt = normalizeWallClockTime(pickupTime) ?? "00:00:00";
  const dt = normalizeWallClockTime(dropoffTime) ?? "00:00:00";
  const start = new Date(`${dateFrom}T${pt}Z`);
  const end = new Date(`${dateTo}T${dt}Z`);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const diff = (end.getTime() - start.getTime()) / 3_600_000;
  return diff > 0 ? diff : 0;
}

/**
 * Task #6588 — true when BOTH pickup and dropoff times are present and
 * parseable wall-clock times. `elapsedHoursBetween` silently defaults a
 * missing time to midnight, so multi-day event quotes with omitted times
 * still produce a positive elapsed value; server quote paths must use
 * this predicate to reject such requests with QUOTE_HOURS_REQUIRED
 * instead of billing a duration nobody stated.
 */
export function hasExplicitQuoteTimes(
  pickupTime?: string | null,
  dropoffTime?: string | null,
): boolean {
  return (
    normalizeWallClockTime(pickupTime) !== null &&
    normalizeWallClockTime(dropoffTime) !== null
  );
}

function normalizeWallClockTime(time?: string | null): string | null {
  if (time == null) return null;
  const trimmed = String(time).trim();
  if (!trimmed) return null;
  const m24 = trimmed.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?$/);
  if (m24) {
    const h = parseInt(m24[1], 10);
    const min = m24[2] ? parseInt(m24[2], 10) : 0;
    const sec = m24[3] ? parseInt(m24[3], 10) : 0;
    if (h < 24 && min < 60 && sec < 60) {
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    }
  }
  const m12 = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = m12[2] ? parseInt(m12[2], 10) : 0;
    const period = m12[3].toLowerCase();
    if (h >= 1 && h <= 12 && min < 60) {
      if (period === "pm" && h !== 12) h += 12;
      if (period === "am" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}:00`;
    }
  }
  return null;
}

/**
 * Format a numeric amount in a given currency using the runtime's
 * `Intl.NumberFormat` — works in browsers, React Native, and Node.
 *
 * Returns an empty string for invalid inputs so callers can fall back
 * gracefully without leaking "NaN" into the UI.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string,
  locale = "en-GB",
): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "";
  const code = String(currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    // Bad currency code — fall back to a code-suffixed plain number so
    // we never throw out of a render.
    return `${Number(amount).toFixed(2)} ${code}`;
  }
}
